import { put, select, call } from 'redux-saga/effects';
import { selectConnection } from 'store/core/selectors';
import {
  removeGraphItem,
  setGraphItem,
  setIsLoadingGraphItems,
} from 'store/graph/actions';
import { selectContextsObject } from 'store/graph/selectors';
import { getItems } from 'utils/graph-helper';
import { readBlob } from 'utils/query-helper';
import { setError } from 'store/core/actions';
import { getErrorMessage } from 'utils/error-helper';
import { isConnectionReady } from 'utils/connection-helper';

const buildQuestionId = (nlInput) => {
  const normalizedInput = (nlInput || '').trim().toLowerCase();
  let hash = 2166136261;

  for (let i = 0; i < normalizedInput.length; i += 1) {
    hash ^= normalizedInput.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return `q_${(hash >>> 0).toString(16).padStart(8, '0')}`;
};

const nlQuery = async (connection, action) => {
  if (!isConnectionReady(connection)) return null;
  const { message } = action;
  const contexts = Array.isArray(action?.contexts) ? action.contexts : [];
  const questionId = buildQuestionId(message);
  const res = await connection.invoke('nl_query', message, contexts, questionId);
  if (res) {
    // nl_query may return either a direct blob URL (legacy) or an object
    // payload with a graphUri field.
    const blobUrl = typeof res === 'string' ? res : res?.graphUri;
    if (!blobUrl) {
      return null;
    }
    return await readBlob(blobUrl, true);
  }
  return res;
};

export function* nlQuerySaga(action) {
  yield put(setIsLoadingGraphItems(true));
  const contexts = yield select(selectContextsObject);
  const connection = yield select(selectConnection);
  const actionContexts = Array.isArray(action?.contexts) ? [...action.contexts] : [];

  // If no explicit @ mentions, auto-populate from currently loaded graph contexts
  // so the backend receives entity IDs for context injection
  if (actionContexts.length === 0 && contexts && Object.keys(contexts).length > 0) {
    for (const [, ctx] of Object.entries(contexts)) {
      if (ctx?.id) actionContexts.push(ctx);
    }
    action = { ...action, contexts: actionContexts };
  }

  try {
    const response = yield call(nlQuery, connection, action);

    // Handle each context separately
    if (response !== null) {
      if (actionContexts.length === 0 && action.type === 'SET_NL_MESSAGE') {
        const messageContext = {
          id: `nl-message-search-${Date.now()}`,
          displayValue: action.message,
          ai_search: true,
        };
        actionContexts.push(messageContext);
      }
      for (const context of actionContexts) {
        const { nodes, links, nodeCount } = getItems(response, context.id);
        if (nodes != null) {
          if (contexts[context.id]) {
            yield put(removeGraphItem(context.id));
          }
          const ai_context = { ...context, ai_search: true };
          yield put(setGraphItem(ai_context, nodes, links, nodeCount));
        }
      }
    }

    yield put(setIsLoadingGraphItems(false));
  } catch (error) {
    console.error('Error in nlQuerySaga:', error);
    const errorMessage = getErrorMessage(error);
    yield put(setError({
      message: errorMessage,
      type: 'nl_query_failed',
      timestamp: Date.now(),
    }));
    yield put(setIsLoadingGraphItems(false));
  }
}
